import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./GmailCard.module.css";
import Api from "../../utils/Api/Api";
import { track as trackEvent } from "../../utils/analyticsService";

/*
 * "Find subscriptions in Gmail" on Account (fleek-backend gmaildetect).
 *
 * Renders nothing unless the backend says this person is offered it
 * (status.available): while Google's verification of gmail.readonly is
 * pending, that is listed testers only. Connect opens Google's consent in
 * this tab; the backend sends the browser back here with ?result=.
 * Found charges are suggestions until the person ticks and adds them.
 *
 * A scan runs in the background on the server (it takes minutes on a real
 * inbox), so Scan now starts one and the card asks status every few
 * seconds until it has finished, then shows what was found.
 */

/** How often to ask whether a background scan has finished, and for how long. */
const POLL_MS = 3000;
const POLL_FOR_MS = 20 * 60 * 1000;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const outcome = (res) => {
  const failed = res instanceof Error || res?.isAxiosError;
  const response = failed ? res.response : res;
  return {
    ok: !failed && !!response && response.status >= 200 && response.status < 300,
    data: response?.data || null,
  };
};

/** Any failure, thrown or resolved (App's interceptor resolves errors), reads the same. */
const call = async (fn) => {
  try {
    return outcome(await fn());
  } catch (e) {
    return outcome(e);
  }
};

const track = (event, params = {}) => {
  try {
    trackEvent(event, params);
  } catch {
    /* analytics must never block the card */
  }
};

const day = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const returnedWith = () => {
  try {
    return new URLSearchParams(window.location.search).get("result");
  } catch {
    return null;
  }
};

export const GmailCard = ({ pollMs = POLL_MS }) => {
  const [status, setStatus] = useState(null);
  const [found, setFound] = useState([]);
  const [hidden, setHidden] = useState(0);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(() =>
    returnedWith() === "connected" ? "Gmail connected. Scanning your receipts now." : returnedWith() === "error" ? "Gmail didn't connect. Please try again." : ""
  );

  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  const loadFound = useCallback(async () => {
    const { ok, data } = await call(() => Api.GmailDetections());
    const rows = ok && Array.isArray(data?.detections) ? data.detections : [];
    setFound(rows);
    setHidden(ok && Number.isFinite(data?.hidden) ? data.hidden : 0);
    // Ticked only for brands Fleek knows; a recurring unknown charge is
    // offered, not assumed. (An older backend sends no "known": tick all.)
    setPicked(Object.fromEntries(rows.map((d) => [d.id, d.known === undefined ? true : !!d.known])));
  }, []);

  // Ask until the background scan is done, then show what it found.
  const waitForScan = useCallback(async () => {
    setBusy("scan");
    const until = Date.now() + POLL_FOR_MS;
    while (alive.current && Date.now() < until) {
      await wait(pollMs);
      const { ok, data } = await call(() => Api.GmailStatus());
      if (!alive.current) return;
      if (ok && data && !data.scanning) {
        setStatus(data);
        setBusy("");
        track("gmail_scanned", { opened: data.last_scan?.opened, detected: data.last_scan?.detected });
        await loadFound();
        const scanned = data.last_scan || {};
        setNote(
          scanned.capped
            ? "Read your most recent receipts; some older ones weren't reached."
            : scanned.detected
            ? ""
            : "No new subscription charges found."
        );
        return;
      }
    }
    if (alive.current) {
      setBusy("");
      setNote("Still scanning your receipts. Check back in a few minutes.");
    }
  }, [loadFound, pollMs]);

  const scan = useCallback(async () => {
    setBusy("scan");
    setNote("");
    const { ok, data } = await call(() => Api.GmailScan());
    if (!ok) {
      setBusy("");
      setNote("The scan didn't start. Please try again.");
      return;
    }
    if (data?.scanning) {
      await waitForScan();
      return;
    }
    // An older backend answers with the finished scan's counts.
    setBusy("");
    track("gmail_scanned", { opened: data?.opened, detected: data?.detected });
    await loadFound();
    setNote(data?.detected ? "" : "No new subscription charges found.");
  }, [loadFound, waitForScan]);

  // Back from Google with a newly connected inbox: scan straight away, once,
  // instead of making the person find the button.
  const autoScanned = useRef(false);

  const loadStatus = useCallback(async () => {
    const { ok, data } = await call(() => Api.GmailStatus());
    if (!ok || !data) return;
    setStatus(data);
    if (data.scanning) {
      waitForScan();
      return;
    }
    if (!autoScanned.current && returnedWith() === "connected" && data.connected) {
      autoScanned.current = true;
      try {
        window.history.replaceState(null, "", window.location.pathname);
      } catch {
        /* the URL keeps ?result=; a reload would only rescan */
      }
      scan();
      return;
    }
    if (data.connected && data.pending) loadFound();
  }, [loadFound, waitForScan, scan]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (!status?.available) return null;

  // Every connected inbox. An older backend sends only the one address.
  const inboxes = Array.isArray(status.links)
    ? status.links
    : status.connected && status.email
    ? [{ id: null, email: status.email, last_scanned_at: status.last_scanned_at }]
    : [];
  const canAdd = status.can_add ?? !status.connected;

  const connect = async () => {
    setBusy("connect");
    track("gmail_connect_tapped");
    const { ok, data } = await call(() => Api.GmailConnect());
    if (ok && data?.authorization_url) {
      window.location.assign(data.authorization_url);
      return;
    }
    setBusy("");
    setNote("Gmail isn't available right now.");
  };

  const keep = async () => {
    const ids = found.filter((d) => picked[d.id]).map((d) => d.id);
    if (!ids.length) return;
    // Everything on the card, so what was left unticked teaches as much as what was kept.
    const shown = found.map((d) => d.id);
    setBusy("keep");
    const { ok, data } = await call(() => Api.GmailKeep(ids, shown));
    setBusy("");
    if (!ok) {
      setNote("Those didn't save. Please try again.");
      return;
    }
    track("gmail_kept", { count: ids.length });
    setNote(`${data?.added ?? ids.length} added to your subscriptions.`);
    await loadFound();
  };

  // One inbox when it has an id; every inbox for an older backend that
  // knows of only one.
  const disconnect = async (inbox) => {
    setBusy("disconnect");
    await call(() => Api.GmailDisconnect(inbox?.id ?? undefined));
    track("gmail_disconnected", { several: inboxes.length > 1 });
    setBusy("");
    setFound([]);
    setNote(
      inbox?.id && inboxes.length > 1
        ? `${inbox.email} disconnected. Fleek no longer has access to it.`
        : "Gmail disconnected. Fleek no longer has access."
    );
    await loadStatus();
    loadFound();
  };

  return (
    <section className={styles.card} aria-labelledby="gmail-card-title">
      <div className={styles.head}>
        <h2 id="gmail-card-title" className={styles.title}>Find subscriptions in Gmail</h2>
        <span className={styles.badge}>Beta</span>
      </div>

      {!status.connected ? (
        <>
          <p className={styles.body}>
            Fleek searches your receipts for subscription charges and shows you what it finds. We keep only the
            merchant, amount, currency and date, never your emails. Read-only, and you can disconnect any time.
            Connect more than one inbox if your receipts are split, say personal and work.
          </p>
          <button type="button" className={styles.primary} onClick={connect} disabled={!!busy}>
            {busy === "connect" ? "Opening Google…" : "Connect Gmail"}
          </button>
        </>
      ) : (
        <>
          <p className={styles.body}>
            {inboxes.length > 1 ? `${inboxes.length} inboxes connected. Scan now reads them all.` : "Connected:"}
          </p>
          <ul className={styles.inboxes}>
            {inboxes.map((inbox) => (
              <li key={inbox.id ?? inbox.email} className={styles.inbox}>
                <span className={styles.inboxEmail}>{inbox.email}</span>
                <span className={styles.date}>
                  {inbox.last_scanned_at ? `scanned ${day(inbox.last_scanned_at)}` : "not scanned yet"}
                </span>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => disconnect(inbox)}
                  disabled={!!busy}
                  aria-label={`Disconnect ${inbox.email}`}
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
          {found.length > 0 && (
            <ul className={styles.list}>
              {found.map((d) => (
                <li key={d.id}>
                  <label className={styles.item}>
                    <input
                      type="checkbox"
                      checked={!!picked[d.id]}
                      onChange={(e) => setPicked((p) => ({ ...p, [d.id]: e.target.checked }))}
                    />
                    <span className={styles.merchant}>{d.merchant}</span>
                    <span className={styles.amount}>
                      {d.amount} {d.currency}
                    </span>
                    <span className={styles.date}>
                      {day(d.charged_at)}
                      {d.receipts > 1 ? ` · ${d.receipts} receipts` : ""}
                      {d.recurring ? " · recurring" : ""}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {hidden > 0 && (
            <p className={styles.hint}>
              {hidden} other {hidden === 1 ? "charge" : "charges"} left out: one-offs, payments to people and invoices.
            </p>
          )}
          <div className={styles.actions}>
            {found.length > 0 && (
              <button type="button" className={styles.primary} onClick={keep} disabled={!!busy || !found.some((d) => picked[d.id])}>
                {busy === "keep" ? "Adding…" : "Add selected"}
              </button>
            )}
            {/* Always there: a new inbox, new receipts, or better rules all
                need another scan, suggestions or not. */}
            <button type="button" className={found.length > 0 ? styles.secondary : styles.primary} onClick={scan} disabled={!!busy}>
              {busy === "scan" ? "Scanning your receipts…" : found.length > 0 ? "Scan again" : "Scan now"}
            </button>
            {canAdd && (
              <button type="button" className={styles.secondary} onClick={connect} disabled={!!busy}>
                {busy === "connect" ? "Opening Google…" : "Add another Gmail"}
              </button>
            )}
          </div>
        </>
      )}
      {note && (
        <p className={styles.note} role="status">
          {note}
        </p>
      )}
    </section>
  );
};

export default GmailCard;
